import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Map, Briefcase } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { sendEmail } from '../utils/sendEmail';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import RateLimitMessage from '../components/RateLimitMessage';
import { checkRateLimit, logAICall } from '../lib/rateLimits';
import './ValuationPage.css';

const STEPS_COUNT = 6;

const NEIGHBOURHOOD_PRICES = [
  { names: ['germasogeia','germasoya'],                 city:'Limassol',  avgPrice:3200 },
  { names: ['mesa geitonia','mesa-geitonia'],           city:'Limassol',  avgPrice:2100 },
  { names: ['agios tychon','agio tychon','ag tychon'],  city:'Limassol',  avgPrice:2750 },
  { names: ['polemidia'],                               city:'Limassol',  avgPrice:1650 },
  { names: ['neapolis'],                                city:'Limassol',  avgPrice:2350 },
  { names: ['strovolos'],                               city:'Nicosia',   avgPrice:1850 },
  { names: ['engomi'],                                  city:'Nicosia',   avgPrice:2400 },
  { names: ['latsia'],                                  city:'Nicosia',   avgPrice:1550 },
  { names: ['geri'],                                    city:'Nicosia',   avgPrice:1250 },
  { names: ['kato paphos','kato pafos'],                city:'Paphos',    avgPrice:2800 },
  { names: ['chloraka'],                                city:'Paphos',    avgPrice:2200 },
  { names: ['peyia','pegia'],                           city:'Paphos',    avgPrice:1980 },
  { names: ['mesa chorio','mesa chorion'],              city:'Paphos',    avgPrice:1750 },
  { names: ['mackenzie','mckenzie'],                    city:'Larnaca',   avgPrice:2000 },
  { names: ['aradippou','aradipou'],                    city:'Larnaca',   avgPrice:1400 },
  { names: ['finikoudes'],                              city:'Larnaca',   avgPrice:2200 },
  { names: ['livadia'],                                 city:'Larnaca',   avgPrice:1600 },
  { names: ['ayia napa','agia napa'],                   city:'Famagusta', avgPrice:2400 },
  { names: ['protaras'],                                city:'Famagusta', avgPrice:2100 },
  { names: ['paralimni'],                               city:'Famagusta', avgPrice:1500 },
];

const CITY_FALLBACK = { Limassol:2600, Paphos:2200, Nicosia:1900, Larnaca:1850, Famagusta:1900 };

// Coastal cities — used to gate the "distance to sea" field and the rent
// yield seafront discount. Nicosia is deliberately excluded.
const COASTAL_CITIES = ['Limassol', 'Larnaca', 'Paphos', 'Ayia Napa', 'Protaras', 'Paralimni'];

// Sale-price features. "Covered Parking" is intentionally removed — parking
// is now captured as a first-class dropdown (parkingType) with a wider
// impact range (-3% to +6%) instead of the previous flat +6% bonus.
const FEATURES = [
  { label:'Sea View',          tKey:'seaView',        bonus:0.12, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg> },
  { label:'Private Pool',      tKey:'privatePool',    bonus:0.10, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20c2-2 4-2 6 0s4 2 6 0 4-2 6 0"/><path d="M2 16c2-2 4-2 6 0s4 2 6 0 4-2 6 0"/><path d="M9 8h.01M15 5h.01"/><path d="M8 5c0-1.1.9-2 2-2h4a2 2 0 0 1 2 2v3H8V5z"/></svg> },
  { label:'Communal Pool',     tKey:'communalPool',   bonus:0.05, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20c2-2 4-2 6 0s4 2 6 0 4-2 6 0"/><path d="M2 16c2-2 4-2 6 0s4 2 6 0 4-2 6 0"/></svg> },
  { label:'Balcony / Terrace', tKey:'balconyTerrace', bonus:0.05, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><rect x="9" y="14" width="6" height="8"/></svg> },
  { label:'Air Conditioning',  tKey:'airConditioning',bonus:0.03, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="8" rx="2"/><path d="M7 14v4m5-4v4m5-4v4"/></svg> },
  { label:'Solar Panels',      tKey:'solarPanels',    bonus:0.04, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M18.66 5.34l1.41-1.41"/></svg> },
  { label:'Fireplace',         tKey:'fireplace',      bonus:0.03, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2c0 4-4 6-4 10a4 4 0 0 0 8 0c0-4-4-6-4-10z"/><path d="M12 12c0 2-1 3-1 4a1 1 0 0 0 2 0c0-1-1-2-1-4z"/></svg> },
];

const FEATURES_BY_TYPE = {
  Apartment:  ['Sea View', 'Communal Pool', 'Balcony / Terrace', 'Air Conditioning', 'Solar Panels'],
  House:      ['Sea View', 'Private Pool', 'Balcony / Terrace', 'Air Conditioning', 'Solar Panels', 'Fireplace'],
  Villa:      ['Sea View', 'Private Pool', 'Communal Pool', 'Balcony / Terrace', 'Air Conditioning', 'Solar Panels', 'Fireplace'],
  Commercial: ['Sea View', 'Air Conditioning', 'Solar Panels'],
  Land:       ['Sea View'],
};

const CONDITIONS_BY_TYPE = {
  default: [
    { value: 'Brand New',        tLabel: 'conditions.brandNew',       tDesc: 'conditions.brandNewDesc'       },
    { value: 'Excellent',        tLabel: 'conditions.excellent',       tDesc: 'conditions.excellentDesc'       },
    { value: 'Good',             tLabel: 'conditions.good',            tDesc: 'conditions.goodDesc'            },
    { value: 'Needs Renovation', tLabel: 'conditions.needsRenovation', tDesc: 'conditions.needsRenovationDesc' },
  ],
  Land: [
    { value: 'Brand New',        label: 'Prime Location',  desc: 'Excellent position, all permits clear'       },
    { value: 'Excellent',        label: 'Good Location',   desc: 'Desirable area, straightforward to develop'  },
    { value: 'Good',             label: 'Standard',        desc: 'Average accessibility and zoning'            },
    { value: 'Needs Renovation', label: 'Challenging',     desc: 'Remote, restricted, or difficult access'     },
  ],
  Commercial: [
    { value: 'Brand New',        label: 'Move-in Ready',   desc: 'Newly built or fully fitted out'             },
    { value: 'Excellent',        label: 'Excellent',       desc: 'Very well maintained, minimal work needed'   },
    { value: 'Good',             label: 'Good',            desc: 'Functional, some cosmetic updates needed'    },
    { value: 'Needs Renovation', label: 'Needs Fit-out',   desc: 'Shell condition or major works required'     },
  ],
};

// ── Adjustment tables (values are decimal deltas, e.g. 0.12 = +12%) ──
const YEAR_BUILT_OPTIONS = [
  { value: '2020_newer',  tKey: 'yearBuilt.opt2020newer' },
  { value: '2010_2019',   tKey: 'yearBuilt.opt2010_2019' },
  { value: '2000_2009',   tKey: 'yearBuilt.opt2000_2009' },
  { value: '1990_1999',   tKey: 'yearBuilt.opt1990_1999' },
  { value: 'before_1990', tKey: 'yearBuilt.optBefore1990' },
  { value: 'unknown',     tKey: 'yearBuilt.optUnknown' },
];
const YEAR_BUILT_ADJ = {
  '2020_newer': 0.12, '2010_2019': 0.05, '2000_2009': 0,
  '1990_1999': -0.08, 'before_1990': -0.18, 'unknown': 0,
};

const ENERGY_OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'unknown'];
const ENERGY_ADJ = { A: 0.08, B: 0.05, C: 0, D: -0.03, E: -0.06, F: -0.12, G: -0.12, unknown: 0 };

const TITLE_DEED_OPTIONS = [
  { value: 'available', tKey: 'titleDeed.optAvailable' },
  { value: 'pending',   tKey: 'titleDeed.optPending' },
  { value: 'none',      tKey: 'titleDeed.optNone' },
];
const TITLE_DEED_ADJ = { available: 0, pending: -0.10, none: -0.25 };

const PARKING_OPTIONS = [
  { value: 'covered', tKey: 'parking.optCovered' },
  { value: 'open',    tKey: 'parking.optOpen' },
  { value: 'street',  tKey: 'parking.optStreet' },
  { value: 'none',    tKey: 'parking.optNone' },
];
const PARKING_ADJ = { covered: 0.06, open: 0.03, street: 0, none: -0.03 };

const FURNISHED_OPTIONS = [
  { value: 'fully',       tKey: 'furnished.optFully' },
  { value: 'partially',   tKey: 'furnished.optPartially' },
  { value: 'unfurnished', tKey: 'furnished.optUnfurnished' },
];
const FURNISHED_SALE_ADJ = { fully: 0.03, partially: 0.01, unfurnished: 0 };
const FURNISHED_RENT_MULT = { fully: 1.20, partially: 1.08, unfurnished: 1.0 };

const SEA_DISTANCE_OPTIONS = [
  { value: 'seafront', tKey: 'seaDistance.optSeafront' },
  { value: 'walking',  tKey: 'seaDistance.optWalking' },
  { value: 'drive',    tKey: 'seaDistance.optDrive' },
  { value: 'inland',   tKey: 'seaDistance.optInland' },
];
const SEA_ADJ = { seafront: 0.40, walking: 0.15, drive: 0.05, inland: 0 };

const ORIENTATION_OPTIONS = [
  { value: 'South',   tKey: 'orientation.optSouth' },
  { value: 'North',   tKey: 'orientation.optNorth' },
  { value: 'East',    tKey: 'orientation.optEast' },
  { value: 'West',    tKey: 'orientation.optWest' },
  { value: 'Corner', tKey: 'orientation.optCorner' },
  { value: 'NotSure', tKey: 'orientation.optNotSure' },
];

const BUILDING_TYPE_OPTIONS = [
  { value: 'Lowrise',   tKey: 'buildingType.optLow' },
  { value: 'Midrise',   tKey: 'buildingType.optMid' },
  { value: 'Highrise',  tKey: 'buildingType.optHigh' },
  { value: 'Boutique',  tKey: 'buildingType.optBoutique' },
];

// Base annual yield by city (apartment baseline). Adjustments for property
// type, sea distance, and furnishing stack multiplicatively on top.
const CITY_YIELD = {
  Limassol: 0.042, Larnaca: 0.058, Paphos: 0.045, Nicosia: 0.055,
  Famagusta: 0.060, 'Ayia Napa': 0.060, Protaras: 0.060, Paralimni: 0.060,
};

const INITIAL_FORM = {
  propertyType:'', city:'', district:'',
  plotSize:'', coveredArea:'', floor:'',
  bedrooms:'', bathrooms:'',
  features:[],
  condition:'', firstName:'', email:'',
  sellingReason: '',
  yearBuilt: '',
  energyRating: '',
  titleDeed: '',
  parkingType: '',
  furnished: '',
  distanceToSea: '',
  renovationYear: '',
  orientation: '',
  buildingType: '',
};

// ═══════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════

function cap(str) {
  return str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function matchNeighbourhood(input) {
  if (!input || input.trim().length < 2) return null;
  const q = input.trim().toLowerCase();
  for (const nb of NEIGHBOURHOOD_PRICES) {
    if (nb.names.some(n => n === q || q.startsWith(n) || n.startsWith(q))) return nb;
  }
  for (const nb of NEIGHBOURHOOD_PRICES) {
    if (nb.names.some(n => q.includes(n) || n.includes(q))) return nb;
  }
  return null;
}

function computeQuartiles(values) {
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  if (!n) return null;
  const at = p => {
    const idx = p * (n - 1);
    const lo = Math.floor(idx), hi = Math.ceil(idx);
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
  };
  return { q1: at(0.25), median: at(0.5), q3: at(0.75), count: n };
}

function findLiveArea(district, byArea) {
  if (!district || !byArea) return null;
  const q = district.trim().toLowerCase();
  const key = Object.keys(byArea).find(k => k.includes(q) || q.includes(k));
  return key ? byArea[key] : null;
}

// Property-type multiplier — kept as a coarse type baseline. The finer-grain
// adjustments (age, energy, deed, parking, sea, furnished, and the optional
// step-5 fields) are handled by the sum-of-adjustments below.
const TYPE_MULT = { Villa:1.30, Apartment:1.0, House:1.08, Commercial:0.85, Land:0.45 };
const CONDITION_MULT = { 'Brand New':1.2, Excellent:1.1, Good:1.0, 'Needs Renovation':0.75 };

function estimatePrice(form, marketData) {
  const covered = Number(form.coveredArea) || 80;
  const plot = Number(form.plotSize) || 0;
  const plotBonus = (plot > covered && form.propertyType !== 'Land')
    ? Math.min((plot / covered - 1) * 0.04, 0.20) : 0;

  let base, q1, q3, sourceLabel, isNeighbourhood = false, liveCount = 0;

  const liveArea = findLiveArea(form.district, marketData?.byArea);
  if (liveArea) {
    base = liveArea.median; q1 = liveArea.q1; q3 = liveArea.q3;
    liveCount = liveArea.count; sourceLabel = cap(form.district); isNeighbourhood = true;
  } else if (marketData?.city) {
    base = marketData.city.median; q1 = marketData.city.q1; q3 = marketData.city.q3;
    liveCount = marketData.city.count; sourceLabel = form.city + ' city avg';
  } else {
    const match = matchNeighbourhood(form.district);
    base = match ? match.avgPrice : (CITY_FALLBACK[form.city] || 2000);
    q1 = base * 0.94; q3 = base * 1.06;
    sourceLabel = match ? cap(match.names[0]) : (form.city + ' city avg');
    isNeighbourhood = !!match;
  }

  // ── Sum-of-adjustments approach ──
  // Applied as (1 + Σ delta) rather than serial multiplication so that
  // effects are additive and easier to reason about. Capped at ±cap so a
  // pathological combination of maxed inputs can't blow up the estimate.
  const adjustments = [];
  const addAdj = (label, value, category) => {
    if (Math.abs(value) < 1e-9) return;
    adjustments.push({ label, value, category });
  };

  const tm = TYPE_MULT[form.propertyType] || 1.0;
  const cm = CONDITION_MULT[form.condition] || 1.0;
  const ff = form.features || [];
  const featBonus = FEATURES.reduce((s, f) => s + (ff.includes(f.label) ? f.bonus : 0), 0);

  addAdj('Property type', tm - 1, 'type');
  addAdj('Condition',     cm - 1, 'condition');
  if (plotBonus) addAdj('Plot bonus', plotBonus, 'plot');
  if (featBonus) addAdj('Features',   featBonus, 'features');

  const yearAdj = YEAR_BUILT_ADJ[form.yearBuilt] ?? 0;
  addAdj('Age (year built)', yearAdj, 'year');

  const energyAdj = ENERGY_ADJ[form.energyRating] ?? 0;
  addAdj('Energy rating', energyAdj, 'energy');

  const titleAdj = TITLE_DEED_ADJ[form.titleDeed] ?? 0;
  addAdj('Title deed', titleAdj, 'title');

  const parkingAdj = PARKING_ADJ[form.parkingType] ?? 0;
  addAdj('Parking', parkingAdj, 'parking');

  const coastal = COASTAL_CITIES.includes(form.city);
  const seaAdj = coastal ? (SEA_ADJ[form.distanceToSea] ?? 0) : 0;
  if (coastal) addAdj('Distance to sea', seaAdj, 'sea');

  const furnAdj = FURNISHED_SALE_ADJ[form.furnished] ?? 0;
  addAdj('Furnished', furnAdj, 'furnished');

  // Optional Step 5 fields — count how many the user filled in so we can
  // boost confidence when both are provided. Max is 2 (orientation + building
  // type for apartments; 1 for houses; 0 for other property types).
  let stage5Count = 0;
  const orientationApplicable = form.propertyType === 'Apartment' || form.propertyType === 'House';
  if (orientationApplicable && form.orientation && form.orientation !== 'NotSure') {
    const oAdj = (form.orientation === 'South' || form.orientation === 'Corner') ? 0.03 : 0;
    addAdj('Orientation', oAdj, 'orientation');
    stage5Count++;
  }
  const buildingApplicable = form.propertyType === 'Apartment';
  if (buildingApplicable && form.buildingType) {
    const bAdj = (form.buildingType === 'Boutique' || form.buildingType === 'Highrise') ? 0.03 : 0;
    addAdj('Building type', bAdj, 'building');
    stage5Count++;
  }

  const rawTotal = adjustments.reduce((sum, a) => sum + a.value, 0);
  const cappedTotal = Math.max(-0.50, Math.min(0.75, rawTotal));
  const capped = cappedTotal !== rawTotal;
  const mult = 1 + cappedTotal;

  const saleLow = Math.round(covered * q1 * mult);
  const saleHigh = Math.round(covered * q3 * mult);
  const sale = Math.round(covered * base * mult);

  // ── Rent yield ──
  // City base yield, then property-type mult, then sea/inland, then
  // furnishing. Rent range = monthly ±10%.
  let annualYield = CITY_YIELD[form.city] ?? 0.048;
  if (form.propertyType === 'House') annualYield *= 0.85;
  else if (form.propertyType === 'Villa') annualYield *= 0.75;
  else if (form.propertyType === 'Commercial') annualYield *= 1.1;

  if (coastal) {
    if (form.distanceToSea === 'seafront') annualYield *= 0.65;
    else if (form.distanceToSea === 'inland') annualYield *= 1.05;
  }
  const furnRentMult = FURNISHED_RENT_MULT[form.furnished] ?? 1.0;
  const monthly = (sale * annualYield * furnRentMult) / 12;
  const rentLow = Math.round(monthly * 0.9);
  const rentHigh = Math.round(monthly * 1.1);

  // ── Confidence tier ──
  let confidence;
  if (liveCount >= 30 && isNeighbourhood && stage5Count >= 2) confidence = 'high';
  else if ((liveCount >= 10 && isNeighbourhood) || liveCount >= 20) confidence = 'medium';
  else if (liveCount >= 3) confidence = 'low';
  else confidence = 'estimate';

  const fmt = n => '€' + n.toLocaleString('en');
  return {
    saleRange:      fmt(saleLow) + ' – ' + fmt(saleHigh),
    rentRange:      fmt(rentLow) + ' – ' + fmt(rentHigh),
    pricePerSqm:    '€' + Math.round(sale/covered).toLocaleString('en') + ' / m²',
    baseUsed:       Math.round(base),
    saleLow, saleHigh, rentLow, rentHigh, sale,
    pricePerSqmNum: Math.round(sale/covered),
    sourceLabel,
    isNeighbourhood,
    liveCount,
    isLive:         liveCount > 0,
    adjustments,
    rawTotal,
    cappedTotal,
    capped,
    mult,
    covered,
    stage5Count,
    confidence,
    annualYield,
    coastal,
  };
}

// ═══════════════════════════════════════════════════════════════
// Icons
// ═══════════════════════════════════════════════════════════════
function PropertyIcon({ type }) {
  if (type === 'Land')       return <Map size={24} strokeWidth={1.7} />;
  if (type === 'Commercial') return <Briefcase size={24} strokeWidth={1.7} />;
  const icons = {
    Apartment: <><rect x="3" y="2" width="18" height="20" rx="1"/><line x1="9" y1="6" x2="9" y2="6.01"/><line x1="12" y1="6" x2="12" y2="6.01"/><line x1="15" y1="6" x2="15" y2="6.01"/><line x1="9" y1="10" x2="9" y2="10.01"/><line x1="12" y1="10" x2="12" y2="10.01"/><line x1="15" y1="10" x2="15" y2="10.01"/><path d="M9 22v-4h6v4"/></>,
    House:     <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    Villa:     <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><rect x="7" y="14" width="4" height="8"/><rect x="13" y="14" width="4" height="8"/></>,
  };
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{icons[type]||icons.House}</svg>;
}

function ConditionIcon({ type }) {
  const icons = {
    'Brand New':        <><circle cx="12" cy="12" r="10"/><path d="M12 6v6l3 3"/></>,
    'Excellent':        <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    'Good':             <><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></>,
    'Needs Renovation': <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></>,
  };
  return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{icons[type]||icons.Good}</svg>;
}

// ═══════════════════════════════════════════════════════════════
// Success screen (shown after successful submit)
// ═══════════════════════════════════════════════════════════════

function SuccessScreen({ email, onReset }) {
  return (
    <div style={{
      maxWidth: 720,
      margin: '0 auto',
      padding: '80px 24px 96px',
      textAlign: 'center',
    }}>
      <h2 style={{
        fontFamily: "'Playfair Display', 'Georgia', serif",
        fontSize: 'clamp(30px, 4.2vw, 44px)',
        fontWeight: 700,
        lineHeight: 1.15,
        color: '#0E0E10',
        margin: '0 0 28px',
        borderRadius: 0,
        letterSpacing: '-0.01em',
      }}>
        Your estimate is on its way.
      </h2>

      <div style={{
        background: '#FAF9F6',
        color: '#0E0E10',
        padding: '22px 26px',
        border: '1px solid #EFEEE9',
        borderRadius: 0,
        fontSize: 16,
        lineHeight: 1.5,
        margin: '0 0 16px',
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}>
        We've sent your Property Price Estimate to{' '}
        <strong style={{ color: '#0E0E10', wordBreak: 'break-all' }}>{email}</strong>.
      </div>

      <p style={{
        fontSize: 13.5,
        color: '#7A7A82',
        margin: '0 0 40px',
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}>
        Check your inbox in the next minute — if you don't see it, check spam.
      </p>

      <div style={{
        display: 'flex',
        gap: 12,
        justifyContent: 'center',
        flexWrap: 'wrap',
      }}>
        <Link
          to="/find-agent"
          className="val-btn val-btn--next"
          style={{
            borderRadius: 0,
            background: '#B8924A',
            color: '#0E0E10',
            padding: '0 22px',
            flex: '0 0 auto',
            minWidth: 220,
            textDecoration: 'none',
          }}
        >
          Find Agent
        </Link>
        <button
          type="button"
          className="val-btn val-btn--back"
          onClick={onReset}
          style={{
            borderRadius: 0,
            background: 'transparent',
            border: '1.5px solid #0E0E10',
            color: '#0E0E10',
            padding: '0 22px',
            minWidth: 220,
          }}
        >
          Start a new estimate
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════
export default function ValuationPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep]             = useState(1);
  const [submitted, setSubmitted]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [marketData, setMarketData] = useState(null);
  const [rateLimitResult, setRateLimitResult] = useState(null);

  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [agentContactConsent, setAgentContactConsent] = useState(false);

  const PRIVACY_CONSENT_TEXT = "I agree to CosmoEstates' Privacy Policy and Terms of Service, and consent to my data being processed to generate this valuation.";
  const AGENT_CONSENT_TEXT = "I allow this information to be forwarded to real estate agents";

  const coastal = COASTAL_CITIES.includes(form.city);
  const orientationApplicable = form.propertyType === 'Apartment' || form.propertyType === 'House';
  const buildingApplicable = form.propertyType === 'Apartment';
  const anyStep5 = orientationApplicable || buildingApplicable;

  useEffect(() => {
    if (!form.city) { setMarketData(null); return; }
    supabase
      .from('listings')
      .select('price, sqm, area')
      .eq('city', form.city)
      .neq('price_label', 'per month')
      .then(({ data }) => {
        if (!data || data.length === 0) { setMarketData(null); return; }
        const pts = data.filter(l => l.price > 0 && l.sqm > 0).map(l => ({
          psm: l.price / l.sqm,
          area: (l.area || '').toLowerCase().trim(),
        }));
        if (!pts.length) { setMarketData(null); return; }
        const cityQ = computeQuartiles(pts.map(p => p.psm));
        const grouped = {};
        pts.forEach(p => {
          if (!p.area) return;
          (grouped[p.area] = grouped[p.area] || []).push(p.psm);
        });
        const byArea = {};
        Object.entries(grouped).forEach(([area, vals]) => {
          if (vals.length >= 3) byArea[area] = computeQuartiles(vals);
        });
        setMarketData({ city: cityQ, byArea, fetchedAt: new Date() });
      });
  }, [form.city]);

  const nbMatch = useMemo(() => matchNeighbourhood(form.district), [form.district]);

  const set = (field, value) => {
    if (field === 'propertyType') {
      const allowed = FEATURES_BY_TYPE[value] || [];
      setForm(p => ({ ...p, propertyType: value, features: p.features.filter(f => allowed.includes(f)) }));
    } else {
      setForm(p => ({ ...p, [field]: value }));
    }
    setErrors(p => ({ ...p, [field]: null }));
  };

  const toggleFeature = label =>
    setForm(p => ({
      ...p,
      features: p.features.includes(label)
        ? p.features.filter(f => f !== label)
        : [...p.features, label],
    }));

  const validate = () => {
    const e = {};
    if (step === 1 && !form.propertyType) e.propertyType = t('valuation.errors.propertyType');
    if (step === 2) {
      if (!form.city) e.city = t('valuation.errors.city');
      if (coastal && !form.distanceToSea) e.distanceToSea = t('valuation.errors.distanceToSea');
    }
    if (step === 3) {
      const needsPlot = !['Apartment', 'Commercial'].includes(form.propertyType);
      if (needsPlot && (!form.plotSize || isNaN(form.plotSize) || Number(form.plotSize) < 1))
        e.plotSize = t('valuation.errors.plotSize');
      if (form.propertyType !== 'Land') {
        if (!form.coveredArea || isNaN(form.coveredArea) || Number(form.coveredArea) < 10)
          e.coveredArea = t('valuation.errors.coveredArea');
      }
      if (!form.yearBuilt) e.yearBuilt = t('valuation.errors.yearBuilt');
    }
    if (step === 4) {
      if (!form.condition) e.condition = t('valuation.errors.condition');
      if (!form.titleDeed) e.titleDeed = t('valuation.errors.titleDeed');
      if (!form.parkingType) e.parkingType = t('valuation.errors.parkingType');
      if (!form.furnished) e.furnished = t('valuation.errors.furnished');
    }
    // Step 5 all optional — no validation.
    if (step === 6) {
      if (!form.firstName.trim()) e.firstName = t('valuation.errors.firstName');
      if (!form.email.trim())     e.email = t('valuation.errors.email');
      else if (!form.email.includes('@')) e.email = t('valuation.errors.emailInvalid');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const submit = async () => {
    if (!validate()) return;
    if (!privacyConsent) {
      alert(t('valuation.privacyAlert'));
      return;
    }

    if (user?.id) {
      const check = await checkRateLimit('valuation', user.id);
      if (!check.allowed) {
        setRateLimitResult(check);
        await logAICall('valuation', user.id, { blocked: true, blockReason: check.reason });
        return;
      }
    }

    setSubmitting(true);

    // Still compute the estimate — its values (saleRange/rentRange/pricePerSqm)
    // flow into the Supabase insert and the email payload. Confidence tier and
    // sample size are also computed here even though they aren't shown on-page
    // anymore.
    const est = estimatePrice(form, marketData);

    setSubmittedEmail(form.email);
    setSubmitted(true);
    setSubmitting(false);

    const nowIso = new Date().toISOString();

    // Consent log — fire-and-forget.
    fetch('/api/log-consent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: form.email,
        user_id: user?.id || null,
        consents: [
          { type: 'privacy_policy', given: true, text: PRIVACY_CONSENT_TEXT },
          {
            type: 'agent_contact',
            given: agentContactConsent,
            text: agentContactConsent ? AGENT_CONSENT_TEXT : 'User declined agent contact consent',
          },
        ],
        property_city: form.city,
        selling_timeframe: form.sellingReason || null,
      }),
    }).catch(err => console.error('Failed to log consent:', err));

    // Save to Supabase — fire-and-forget.
    supabase.from('valuations').insert({
      first_name:    form.firstName,
      email:         form.email,
      city:          form.city,
      district:      form.district || null,
      property_type: form.propertyType,
      covered_area:  Number(form.coveredArea) || null,
      plot_size:     Number(form.plotSize) || null,
      bedrooms:      form.bedrooms || null,
      bathrooms:     form.bathrooms || null,
      condition:     form.condition,
      features:      form.features,
      selling_reason: form.sellingReason || null,
      sale_low:      est.saleLow,
      sale_high:     est.saleHigh,
      rent_low:      est.rentLow,
      rent_high:     est.rentHigh,
      price_per_sqm: est.pricePerSqmNum,
      agent_contact_consent_at_submission: agentContactConsent,
      agent_contact_consent_text: agentContactConsent ? AGENT_CONSENT_TEXT : null,
      agent_contact_consent_timestamp: agentContactConsent ? nowIso : null,
    }).then(({ error }) => { if (error) console.error('Supabase insert failed:', error); });

    if (agentContactConsent && user?.id) {
      supabase
        .from('profiles')
        .update({
          agent_contact_consent: true,
          agent_contact_consent_at: nowIso,
        })
        .eq('id', user.id)
        .then(({ error }) => { if (error) console.error('Profile consent update failed:', error); });
    }

    // Email — payload unchanged.
    sendEmail({
      type: 'valuation',
      to:   form.email,
      data: {
        firstName:    form.firstName,
        saleRange:    est.saleRange,
        rentRange:    est.rentRange,
        pricePerSqm:  est.pricePerSqm,
        city:         form.city,
        propertyType: form.propertyType,
      },
    }).catch(err => console.error('Email failed:', err));

    logAICall('valuation', user?.id, { model: 'deterministic-v2' });
  };

  const resetForm = () => {
    setSubmitted(false);
    setSubmittedEmail('');
    setStep(1);
    setForm(INITIAL_FORM);
    setErrors({});
    setPrivacyConsent(false);
    setAgentContactConsent(false);
  };

  return (
    <div className="val-page">

      <header className="toolHero">
        <div className="toolHero__topBar">
          <button type="button" className="toolHero__back" onClick={() => navigate(-1)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            {t('valuation.back')}
          </button>
        </div>
        <div className="toolHero__inner">
          <span className="toolHero__eyebrow">{t('valuation.heroEyebrow')}</span>
          <h1 className="toolHero__title">{t('valuation.heroTitle')}</h1>
          <p className="toolHero__subtitle">
            {t('valuation.heroSubtitle')}
            <Link to="/find-agent" className="val-pro-link">{t('valuation.requestPro')}</Link>.
          </p>
        </div>
      </header>

      <div className="val-page__content">

        {rateLimitResult && !rateLimitResult.allowed && (
          <RateLimitMessage
            result={rateLimitResult}
            feature="valuation"
            onClose={() => setRateLimitResult(null)}
          />
        )}

        {submitted && (
          <SuccessScreen email={submittedEmail} onReset={resetForm} />
        )}

        {!submitted && !rateLimitResult?.allowed && (
          <div className="val-card">
            <div className="val-progress">
              {Array.from({ length: STEPS_COUNT }).map((_, i) => (
                <div key={i} className={`val-progress__step${step>i+1?' done':''}${step===i+1?' active':''}`}>
                  <div className="val-progress__dot">
                    {step > i+1
                      ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      : i+1}
                  </div>
                </div>
              ))}
            </div>

            <div className="val-body">
              {step === 1 && (
                <div className="val-step">
                  <h3>{t('valuation.step1Title')}</h3>
                  <div className="val-type-grid">
                    {['Apartment','House','Villa','Commercial','Land'].map(type => (
                      <button key={type} className={`val-type-btn${form.propertyType===type?' active':''}`} onClick={() => set('propertyType', type)}>
                        <PropertyIcon type={type} /><span>{t('valuation.propertyTypes.' + type)}</span>
                      </button>
                    ))}
                  </div>
                  {errors.propertyType && <p className="val-err">{errors.propertyType}</p>}
                </div>
              )}

              {step === 2 && (
                <div className="val-step">
                  <h3>{t('valuation.step2Title')}</h3>
                  <div className="val-grid-2">
                    <div className="val-field">
                      <label>{t('valuation.cityLabel')}</label>
                      <select value={form.city} onChange={e => set('city', e.target.value)} className={errors.city?'err':''}>
                        <option value="">{t('valuation.selectCity')}</option>
                        {['Nicosia','Limassol','Paphos','Larnaca','Ayia Napa','Protaras','Paralimni'].map(c => (
                          <option key={c} value={c}>{t('valuation.cities.' + c)}</option>
                        ))}
                      </select>
                      {errors.city && <span className="val-field-err">{errors.city}</span>}
                    </div>
                    <div className="val-field">
                      <label>{t('valuation.areaLabel')} <span className="val-hint">{t('valuation.optional')}</span></label>
                      <input type="text" placeholder={t('valuation.areaPlaceholder')} value={form.district} onChange={e => set('district', e.target.value)} />
                      {nbMatch && (
                        <div className="val-badge val-badge--green">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          {cap(nbMatch.names[0])} matched
                        </div>
                      )}
                      {form.district && !nbMatch && form.district.length > 2 && (
                        <div className="val-badge val-badge--amber">{t('valuation.usingCityAvg').replace('{city}', form.city || '...')}</div>
                      )}
                    </div>
                  </div>
                  {coastal && (
                    <div className="val-field val-field--full">
                      <label>{t('valuation.distanceToSeaLabel')}</label>
                      <select value={form.distanceToSea} onChange={e => set('distanceToSea', e.target.value)} className={errors.distanceToSea?'err':''}>
                        <option value="">{t('valuation.selectOption')}</option>
                        {SEA_DISTANCE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{t('valuation.' + opt.tKey)}</option>
                        ))}
                      </select>
                      {errors.distanceToSea && <span className="val-field-err">{errors.distanceToSea}</span>}
                    </div>
                  )}
                </div>
              )}

              {step === 3 && (
                <div className="val-step">
                  <h3>{t('valuation.step3Title')}</h3>
                  <div className="val-grid-2">
                    <div className="val-step3-left">
                      <div className="val-grid-2">
                        {!['Apartment','Commercial'].includes(form.propertyType) && (
                          <div className="val-field">
                            <label>{t('valuation.plotSize')}</label>
                            <input type="number" placeholder={t('valuation.plotPlaceholder')} value={form.plotSize} onChange={e => set('plotSize', e.target.value)} className={errors.plotSize?'err':''} />
                            {errors.plotSize && <span className="val-field-err">{errors.plotSize}</span>}
                          </div>
                        )}
                        {form.propertyType !== 'Land' && (
                          <div className="val-field">
                            <label>{t('valuation.coveredArea')}</label>
                            <input type="number" placeholder={t('valuation.coveredPlaceholder')} value={form.coveredArea} onChange={e => set('coveredArea', e.target.value)} className={errors.coveredArea?'err':''} />
                            {errors.coveredArea && <span className="val-field-err">{errors.coveredArea}</span>}
                          </div>
                        )}
                        {['Apartment','Commercial'].includes(form.propertyType) && (
                          <div className="val-field">
                            <label>{t('valuation.floorLabel')}</label>
                            <select value={form.floor || ''} onChange={e => set('floor', e.target.value)}>
                              <option value=""></option>
                              {['Ground','1st','2nd','3rd','4th','5th+','Penthouse'].map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                          </div>
                        )}
                        {form.propertyType === 'Land' && (
                          <div className="val-field">
                            <label>{t('valuation.plotSize')}</label>
                            <input type="number" placeholder={t('valuation.landPlotPlaceholder')} value={form.plotSize} onChange={e => set('plotSize', e.target.value)} className={errors.plotSize?'err':''} />
                            {errors.plotSize && <span className="val-field-err">{errors.plotSize}</span>}
                          </div>
                        )}
                      </div>
                      <div className="val-grid-2">
                        {!['Land'].includes(form.propertyType) && (
                          <div className="val-field">
                            <label>{t('valuation.bedroomsLabel')}</label>
                            <select value={form.bedrooms} onChange={e => set('bedrooms', e.target.value)}>
                              <option value=""></option>
                              {['Studio',1,2,3,4,5,'6+'].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                          </div>
                        )}
                        {!['Land'].includes(form.propertyType) && (
                          <div className="val-field">
                            <label>{t('valuation.bathroomsLabel')}</label>
                            <select value={form.bathrooms} onChange={e => set('bathrooms', e.target.value)}>
                              <option value=""></option>
                              {[1,2,3,'4+'].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                          </div>
                        )}
                      </div>
                      <div className="val-grid-2">
                        <div className="val-field">
                          <label>{t('valuation.yearBuiltLabel')}</label>
                          <select value={form.yearBuilt} onChange={e => set('yearBuilt', e.target.value)} className={errors.yearBuilt?'err':''}>
                            <option value="">{t('valuation.selectOption')}</option>
                            {YEAR_BUILT_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{t('valuation.' + opt.tKey)}</option>
                            ))}
                          </select>
                          {errors.yearBuilt && <span className="val-field-err">{errors.yearBuilt}</span>}
                        </div>
                        <div className="val-field">
                          <label>
                            {t('valuation.energyRatingLabel')} <span className="val-hint">{t('valuation.optional')}</span>
                          </label>
                          <select value={form.energyRating} onChange={e => set('energyRating', e.target.value)}>
                            <option value="">{t('valuation.selectOption')}</option>
                            {ENERGY_OPTIONS.map(v => (
                              <option key={v} value={v}>{v === 'unknown' ? t('valuation.dontKnow') : v}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="val-step3-right">
                      <div className="val-section-label">{t('valuation.featuresLabel')}</div>
                      <div className="val-features-grid">
                        {FEATURES.filter(f => (FEATURES_BY_TYPE[form.propertyType] || []).includes(f.label)).map(({ label, tKey, icon }) => {
                          const on = form.features.includes(label);
                          return (
                            <button key={label} type="button" className={`val-feat${on?' active':''}`} onClick={() => toggleFeature(label)}>
                              <span className="val-feat__icon">{icon}</span>
                              <span className="val-feat__label">{t('valuation.features.' + tKey)}</span>
                              {on && <span className="val-feat__check"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="val-step">
                  <h3>{t('valuation.step4Title')}</h3>
                  <div className="val-condition-grid">
                    {(CONDITIONS_BY_TYPE[form.propertyType] || CONDITIONS_BY_TYPE.default).map(c => {
                      const label = c.label || t('valuation.' + c.tLabel);
                      const desc  = c.desc  || t('valuation.' + c.tDesc);
                      return (
                        <button key={c.value} className={`val-cond-btn${form.condition===c.value?' active':''}`} onClick={() => set('condition', c.value)}>
                          <span className="val-cond-btn__icon"><ConditionIcon type={c.value} /></span>
                          <span className="val-cond-btn__label">{label}</span>
                          <span className="val-cond-btn__desc">{desc}</span>
                        </button>
                      );
                    })}
                  </div>
                  {errors.condition && <p className="val-err">{errors.condition}</p>}

                  <div className="val-grid-3">
                    <div className="val-field">
                      <label>{t('valuation.titleDeedLabel')}</label>
                      <select value={form.titleDeed} onChange={e => set('titleDeed', e.target.value)} className={errors.titleDeed?'err':''}>
                        <option value="">{t('valuation.selectOption')}</option>
                        {TITLE_DEED_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{t('valuation.' + opt.tKey)}</option>
                        ))}
                      </select>
                      <span className="val-field-note">{t('valuation.titleDeedTooltip')}</span>
                      {errors.titleDeed && <span className="val-field-err">{errors.titleDeed}</span>}
                    </div>
                    <div className="val-field">
                      <label>{t('valuation.parkingLabel')}</label>
                      <select value={form.parkingType} onChange={e => set('parkingType', e.target.value)} className={errors.parkingType?'err':''}>
                        <option value="">{t('valuation.selectOption')}</option>
                        {PARKING_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{t('valuation.' + opt.tKey)}</option>
                        ))}
                      </select>
                      {errors.parkingType && <span className="val-field-err">{errors.parkingType}</span>}
                    </div>
                    <div className="val-field">
                      <label>{t('valuation.furnishedLabel')}</label>
                      <select value={form.furnished} onChange={e => set('furnished', e.target.value)} className={errors.furnished?'err':''}>
                        <option value="">{t('valuation.selectOption')}</option>
                        {FURNISHED_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{t('valuation.' + opt.tKey)}</option>
                        ))}
                      </select>
                      {errors.furnished && <span className="val-field-err">{errors.furnished}</span>}
                    </div>
                  </div>
                </div>
              )}

              {step === 5 && (
                <div className="val-step">
                  <h3>{t('valuation.step5TitleV2')}</h3>
                  <p className="val-step-sub">{t('valuation.step5SubV2')}</p>
                  {!anyStep5 ? (
                    <div className="val-step5-empty">
                      {t('valuation.step5Empty')}
                    </div>
                  ) : (
                    <div className="val-grid-3">
                      {orientationApplicable && (
                        <div className="val-field">
                          <label>{t('valuation.orientationLabel')}</label>
                          <select value={form.orientation} onChange={e => set('orientation', e.target.value)}>
                            <option value="">{t('valuation.selectOption')}</option>
                            {ORIENTATION_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{t('valuation.' + opt.tKey)}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {buildingApplicable && (
                        <div className="val-field">
                          <label>{t('valuation.buildingTypeLabel')}</label>
                          <select value={form.buildingType} onChange={e => set('buildingType', e.target.value)}>
                            <option value="">{t('valuation.selectOption')}</option>
                            {BUILDING_TYPE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{t('valuation.' + opt.tKey)}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {step === 6 && (
                <div className="val-step">
                  <div className="val-grid-2 val-grid-2--email">
                    <div className="val-email-left">
                      <div className="val-email-icon">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#B8924A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                          <polyline points="22,6 12,13 2,6"/>
                        </svg>
                      </div>
                      <h3>{t('valuation.step5Title')}</h3>
                      <p className="val-email-sub">{t('valuation.step5Sub')}</p>
                    </div>
                    <div className="val-email-right">
                      <div className="val-field">
                        <label>{t('valuation.firstNameLabel')}</label>
                        <input type="text" placeholder={t('valuation.firstNamePlaceholder')} value={form.firstName} onChange={e => set('firstName', e.target.value)} className={errors.firstName?'err':''} />
                        {errors.firstName && <span className="val-field-err">{errors.firstName}</span>}
                      </div>
                      <div className="val-field">
                        <label>{t('valuation.emailLabel')}</label>
                        <input type="email" placeholder={t('valuation.emailPlaceholder')} value={form.email} onChange={e => set('email', e.target.value)} className={errors.email?'err':''} />
                        {errors.email && <span className="val-field-err">{errors.email}</span>}
                      </div>
                      <div className="val-field">
                        <label>{t('valuation.reasonLabel')} <span className="val-hint">{t('valuation.reasonOptional')}</span></label>
                        <div className="val-reason-grid">
                          {['just_exploring', 'thinking_about', 'within_6_months', 'within_1_year'].map(value => (
                            <button
                              key={value}
                              type="button"
                              className={`val-reason-btn${form.sellingReason === value ? ' active' : ''}`}
                              onClick={() => set('sellingReason', form.sellingReason === value ? '' : value)}
                            >
                              {t('valuation.reasons.' + value)}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="val-disclaimer">
                        <strong>{t('valuation.disclaimerNote')}</strong> {t('valuation.disclaimerText')} <strong>{t('valuation.disclaimerNotProfessional')}</strong> {t('valuation.disclaimerTextCont')}
                      </div>

                      <div className="val-consent">
                        <label className="val-consent__label">
                          <input
                            type="checkbox"
                            checked={privacyConsent}
                            onChange={e => setPrivacyConsent(e.target.checked)}
                            className="val-consent__checkbox"
                          />
                          <span className="val-consent__text">
                            {t('valuation.privacyPrefix')}{' '}
                            <Link to="/privacy" target="_blank" rel="noopener noreferrer">{t('valuation.privacyPolicy')}</Link>
                            {' '}{t('valuation.privacyAnd')}{' '}
                            <Link to="/terms" target="_blank" rel="noopener noreferrer">{t('valuation.termsOfService')}</Link>
                            {t('valuation.privacySuffix')}
                            <span className="val-consent__required">*</span>
                          </span>
                        </label>
                      </div>

                      <div className="val-consent val-consent--optional">
                        <label className="val-consent__label">
                          <input
                            type="checkbox"
                            checked={agentContactConsent}
                            onChange={e => setAgentContactConsent(e.target.checked)}
                            className="val-consent__checkbox"
                          />
                          <span className="val-consent__text">
                            {t('valuation.consent.label')}
                          </span>
                        </label>
                        {agentContactConsent && (
                          <div className="val-consent__detail">
                            {t('valuation.consent.detail')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="val-nav">
                {step > 1 && <button className="val-btn val-btn--back" onClick={back}>{t('valuation.back')}</button>}
                {step < STEPS_COUNT
                  ? <button className="val-btn val-btn--next" onClick={next}>{t('valuation.continue')}</button>
                  : <button
                      className="val-btn val-btn--next"
                      onClick={submit}
                      disabled={submitting || !privacyConsent}
                    >
                      {submitting ? t('valuation.sending') : t('valuation.getValuation')}
                    </button>
                }
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
